import { memo, type MouseEvent, type ReactNode } from 'react';

import type { SideNavLink } from '../types';

import { useLinkElement } from '../../../../../context/LinkNavigation/LinkNavigation';
import { isPlainLeftClick } from '../../../../../utils/isPlainLeftClick';

interface SideNavControlProps {
  children: ReactNode;
  className: string;
  isCurrent: boolean;
  link: SideNavLink;
  onActivate: (key: string) => void;
}

/**
 * Renders a nav entry as a real link, so it can be opened in a new tab, copied
 * and announced as a link, and falls back to a button for the entries that act
 * instead of navigating (opening a panel or a dialog).
 */
export const SideNavControl = memo(function SideNavControl({
  children,
  className,
  isCurrent,
  link,
  onActivate,
}: SideNavControlProps) {
  const LinkElement = useLinkElement(link.href);
  const sharedProps = {
    'aria-current': isCurrent ? ('page' as const) : undefined,
    className,
    ...link.dataAttributes,
  };

  if (link.href === undefined) {
    return (
      <button
        type="button"
        {...sharedProps}
        onClick={() => onActivate(link.key)}
      >
        {children}
      </button>
    );
  }

  return (
    <LinkElement
      {...sharedProps}
      href={link.href}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        if (!isPlainLeftClick(event)) {
          return;
        }
        onActivate(link.key);
      }}
    >
      {children}
    </LinkElement>
  );
});
